import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";

function toEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url)) return { type: "video", src: url };
  return { type: "iframe", src: url };
}

export default function GuideDetail() {
  const { id } = useParams();
  const [g, setG] = useState(null);

  useEffect(() => { api.get(`/guides/${id}`).then((r) => setG(r.data)).catch(() => setG(false)); }, [id]);

  if (g === false) return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-brand-forest/50">Guide not found.</div>;
  if (!g) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <div className="rounded-3xl overflow-hidden border border-black/5 shadow-sm mb-8" data-testid="guide-hero">
        {g.cover_image && (
          <div className="h-56 sm:h-72 overflow-hidden bg-brand-sand">
            <img src={mediaUrl(g.cover_image)} alt={g.title} className="h-full w-full object-cover" />
          </div>
        )}
        <div className="p-7 sm:p-8 bg-white">
          {g.appliance && <span className="overline text-brand-terracotta">{g.appliance}</span>}
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">{g.title}</h1>
          {g.description && <p className="text-brand-forest/70 mt-3 leading-relaxed">{g.description}</p>}
        </div>
      </div>

      {g.video_link && (() => {
        const emb = toEmbed(g.video_link);
        return (
          <div className="mb-8 rounded-3xl overflow-hidden border border-black/5 shadow-sm bg-black aspect-video" data-testid="guide-video-embed">
            {emb.type === "video" ? (
              <video src={emb.src} controls className="h-full w-full" />
            ) : (
              <iframe
                src={emb.src}
                title="Guide video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full"
              />
            )}
          </div>
        );
      })()}

      {(g.media || []).length > 0 && (
        <div className="grid sm:grid-cols-2 gap-4 mb-8" data-testid="guide-media">
          {g.media.map((m, i) =>
            m.type === "video" ? (
              <video key={i} controls className="w-full rounded-2xl border border-black/5 bg-black" data-testid={`guide-video-${i}`}>
                <source src={mediaUrl(m.url)} />
              </video>
            ) : (
              <figure key={i} className="rounded-2xl overflow-hidden border border-black/5">
                <img src={mediaUrl(m.url)} alt={m.caption || "Guide media"} className="w-full object-cover" data-testid={`guide-image-${i}`} />
                {m.caption && <figcaption className="text-xs text-brand-forest/60 p-2 bg-white">{m.caption}</figcaption>}
              </figure>
            )
          )}
        </div>
      )}

      <div className="bg-white rounded-3xl p-7 sm:p-8 border border-black/5 shadow-sm">
        <h2 className="font-display text-2xl tracking-tight mb-6">Step-by-Step</h2>
        <ol className="space-y-5">
          {(g.steps || []).map((s, i) => (
            <li key={i} className="flex items-start gap-4" data-testid={`guide-step-${i}`}>
              <span className="mt-0.5 h-9 w-9 shrink-0 rounded-full bg-brand-forest text-white font-display text-lg flex items-center justify-center">{i + 1}</span>
              <p className="text-brand-forest/85 leading-relaxed pt-1">{s}</p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
